import { CHANGE_EVENT, getFavourites, removeFavourite } from '@theme/favourites-store';
import { getCartErrorMessage } from '@theme/cart-feedback';

// Renders saved favourites from the product .js endpoint into the <template> row authored in Liquid.
class SFavouritesList extends HTMLElement {
  #ac;
  #cache = new Map();
  #renderToken = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#render();
    document.addEventListener(CHANGE_EVENT, () => this.#render(), opts);
    window.addEventListener('storage', () => this.#render(), opts);

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[data-favourite-action]') : null;
      if (!button) return;
      const row = button.closest('[data-handle]');
      if (!row) return;

      if (button.dataset.favouriteAction === 'remove') {
        removeFavourite(row.dataset.handle);
      } else if (button.dataset.favouriteAction === 'add') {
        this.#addToCart(button);
      }
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #fetchProduct(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
    // The .js suffix is required for the JSON product contract.
    const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: this.#ac.signal })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.#cache.set(handle, request);
    return request;
  }

  #formatMoney(cents) {
    // Product JSON returns minor units in the shopper's currency; format with the storefront locale.
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'CAD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  async #render() {
    const token = ++this.#renderToken;
    const list = this.querySelector('[data-favourites-items]');
    const empty = this.querySelector('[data-favourites-empty]');
    const loading = this.querySelector('[data-favourites-loading]');
    const template = this.querySelector('template');
    if (!list || !template) return;

    const handles = getFavourites();
    if (empty) empty.hidden = handles.length > 0;
    if (!handles.length) {
      list.replaceChildren();
      if (loading) loading.hidden = true;
      return;
    }

    if (loading && !list.children.length) loading.hidden = false;
    const products = await Promise.all(handles.map((handle) => this.#fetchProduct(handle)));
    if (token !== this.#renderToken) return;
    if (loading) loading.hidden = true;

    const rows = [];
    products.forEach((product, index) => {
      if (!product) return;
      rows.push(this.#row(template, handles[index], product));
    });
    list.replaceChildren(...rows);
    if (empty) empty.hidden = rows.length > 0;
  }

  #row(template, handle, product) {
    const fragment = template.content.cloneNode(true);
    const row = fragment.querySelector('[data-handle]');
    row.dataset.handle = handle;

    const variant = product.variants.find((item) => item.available) || product.variants[0];
    const singleVariant = product.variants.length === 1;

    fragment.querySelectorAll('[data-favourite-link]').forEach((link) => {
      link.href = product.url;
    });
    const title = fragment.querySelector('[data-favourite-title]');
    if (title) title.textContent = product.title;

    const image = fragment.querySelector('[data-favourite-image]');
    const imageSrc = product.featured_image;
    if (image && imageSrc) {
      const url = new URL(imageSrc, window.location.href);
      url.searchParams.set('width', '160');
      image.src = url.toString();
      image.alt = product.title;
    } else if (image) {
      image.remove();
    }

    const price = fragment.querySelector('[data-favourite-price]');
    if (price && variant) price.textContent = this.#formatMoney(variant.price);

    const remove = fragment.querySelector('[data-favourite-action="remove"]');
    if (remove) remove.setAttribute('aria-label', (this.dataset.removeLabel || '').replace('__TITLE__', product.title));

    const add = fragment.querySelector('[data-favourite-action="add"]');
    const choose = fragment.querySelector('[data-favourite-choose]');
    const soldOut = fragment.querySelector('[data-favourite-sold-out]');

    if (!variant || !variant.available) {
      add?.remove();
      choose?.remove();
      if (soldOut) soldOut.hidden = false;
    } else if (!singleVariant) {
      add?.remove();
      if (choose) choose.hidden = false;
    } else {
      choose?.remove();
      if (add) {
        add.dataset.variantId = String(variant.id);
        add.setAttribute('aria-label', (this.dataset.addLabel || '').replace('__TITLE__', product.title));
      }
    }

    return fragment;
  }

  async #addToCart(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    button.classList.add('opacity-50', 'cursor-wait');
    let message = this.dataset.addedMessage;

    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines: [{ merchandiseId: variantId, quantity: 1 }] },
          { event: { context: 'dialog', detail: { source: 's-favourites-list' } } }
        );
        const userError = result?.userErrors?.[0]?.message;
        if (userError) message = userError;
      } else {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!response.ok) message = this.dataset.errorMessage;
      }
    } catch (error) {
      message = getCartErrorMessage(error) || this.dataset.errorMessage;
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('opacity-50', 'cursor-wait');
    }

    if (message) {
      document.dispatchEvent(new CustomEvent('s-notification:add', {
        detail: { message, variant: 'cart', duration: 5000 },
      }));
    }
  }
}

if (!customElements.get('s-favourites-list')) {
  customElements.define('s-favourites-list', SFavouritesList);
}
